import * as React from 'react';

import { connect, useDispatch } from 'react-redux';

import Dinero from 'dinero.js';

// Store
import AppState from '@webapp/store/state';
import { getFullName } from '@webapp/store/user/selectors';

// Types
import User from '@common/types/user/user';

// Transalte
import { Translate } from 'react-localize-redux';

// Controllers
import UserAvatarInfo from '@components/basics/user-avatar-info';

// Components
import Flex from '@components/layout/flex';
import NewMenu from '@components/new-menu';
import Button from '@components/actions/button';
import Typography from '@components/basics/typography';

import WithdrawMenuContent from './menu-content';
import { CloseMenu, ResetError } from '@webapp/store/ui/action-creators';
import { withdrawalInitiated } from '@webapp/utils/rudderstack/events';
import { serializeAmount } from '../../../store/stripe-connect/utils';
import {
  MIN_US_TRANSFER_OBJECT,
  MIN_INTL_TRANSFER_OBJECT,
} from '@services/stripe-connect/constants';
import { isAccountFromCountry } from '@services/stripe-connect/lib/is-account-from-country';
import { CreateTransfer } from '../../../store/stripe-connect/action-creators';
import { CREATE_TRANSFER_ERROR } from '../../../store/stripe-connect/actions';
import { isConnectedAccountReady } from '../../../store/stripe-connect/selectors';
import { ConnectedAccount } from '@services/stripe-connect/types/connected-account';

interface UserWithdrawMenuProps extends Partial<User> {
  className?: string;
  disabled?: boolean;
  account?: ConnectedAccount;
  available: Dinero.Dinero;
  lastTX?: number;
  errorMsg?: string;
}

const mapStateToProps = (state: AppState) => ({
  account: state.stripeConnect.account,
  avatar: state.user.profile?.avatar,
  fullName: getFullName(state),
  available: state.royalties.balance,
  lastTX: state.stripeConnect.lastTx,
  errorMsg: state.ui.errors[CREATE_TRANSFER_ERROR],
});

export const UserWithdrawMenu: React.FC<UserWithdrawMenuProps> = ({
  className,
  account,
  avatar,
  fullName,
  available,
  disabled,
  errorMsg,
  lastTX,
}) => {
  const dispatch = useDispatch();

  const [txAmount, setTxAmount] = React.useState<string>('');

  const isSuccess = false; // TODO: add success state

  const openMenu = async () => {
    setTxAmount('');
    await withdrawalInitiated();
  };

  const close = React.useCallback(() => {
    dispatch(new ResetError(CREATE_TRANSFER_ERROR));
    dispatch(new CloseMenu());
  }, [dispatch]);

  const isDisabled = () => {
    if (errorMsg) {
      return true;
    }

    if (!account) {
      return true;
    }

    if (!isConnectedAccountReady(account)) {
      return true;
    }

    if (txAmount) {
      const money = serializeAmount(txAmount);
      if (isAccountFromCountry(account, 'US')) {
        if (money.lessThan(Dinero(MIN_US_TRANSFER_OBJECT))) {
          return true;
        }
      } else if (isAccountFromCountry(account, 'NG')) {
        if (money.lessThan(Dinero(MIN_US_TRANSFER_OBJECT))) {
          return true;
        }
      } else {
        if (money.lessThan(Dinero(MIN_INTL_TRANSFER_OBJECT))) {
          return true;
        }
      }
      if (money.greaterThan(available)) {
        return true;
      }
      return false;
    }
    return true;
  };

  const createTransfer = () => {
    dispatch(new CreateTransfer(serializeAmount(txAmount)));
  };

  React.useEffect(() => {
    if (lastTX) {
      close();
    }
  }, [lastTX, close]);

  return (
    <NewMenu
      className={className}
      button={
        <Button
          buttonType="primary"
          uppercase
          autoWidth
          disabled={disabled}
          customHeight={50}
          onClick={openMenu}
        >
          <Translate id="USER_PROFILE.WALLET.FUNDS.BUTTON" />
        </Button>
      }
      menuId="USER_WITHDRAW"
      header={
        <>
          {avatar && (
            <UserAvatarInfo
              className={!isSuccess ? 'mb-30' : ''}
              src={avatar}
              name={fullName!}
            />
          )}
          {!isSuccess && (
            <Typography type="analytics-nav" customFont="semi-bold">
              <Translate id="USER_PROFILE.WALLET.WITHDRAW.MENU.TITLE" />
            </Typography>
          )}
        </>
      }
      footer={
        !isSuccess ? (
          <Flex gap={5}>
            <Button height={60} buttonType="secondary" onClick={close}>
              <Translate id="USER_PROFILE.WALLET.WITHDRAW.MENU.ACTIONS.CANCEL" />
            </Button>
            <Button
              height={60}
              buttonType="primary"
              disabled={isDisabled()}
              onClick={createTransfer}
            >
              <Translate id="USER_PROFILE.WALLET.WITHDRAW.MENU.ACTIONS.WITHDRAW" />
            </Button>
          </Flex>
        ) : (
          <Flex
            justifyContent="center"
            alignItems="center"
            style={{ height: '100%' }}
          >
            <Typography align="center">
              <Translate id="USER_PROFILE.WALLET.WITHDRAW.MENU.PROCESSING.REDIRECT" />
            </Typography>
          </Flex>
        )
      }
    >
      <WithdrawMenuContent
        txAmount={txAmount}
        onChange={setTxAmount}
        errorMsg={errorMsg}
      />
    </NewMenu>
  );
};

export default connect(mapStateToProps)(UserWithdrawMenu);
